import { useMemo, type ComponentProps } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@/lib/utils.js';

export type SliderProps = ComponentProps<typeof SliderPrimitive.Root> & {
  /**
   * Names the thumb, which is the element that actually carries
   * `role="slider"`.
   *
   * Radix puts the role on the thumb and does not forward the root's
   * `aria-label` to it, and its own default label is `undefined` for a
   * single-thumb slider — so without this a one-thumb slider has no accessible
   * name at all. A string names every thumb; pass an array to name them
   * individually, which is what a range slider wants ('Minimum', 'Maximum').
   */
  thumbLabel?: string | readonly string[];
};

/**
 * Radix Slider with the console's treatment.
 *
 * The thumb count is derived from the value so a range slider gets two
 * handles without the caller saying so, as in stock shadcn. `value` and
 * `defaultValue` are read from `props` rather than destructured, because
 * `exactOptionalPropertyTypes` forbids handing an explicit `undefined` back to
 * a prop typed `number[]`.
 */
export function Slider({ className, thumbLabel, ...props }: SliderProps) {
  const { value, defaultValue, min = 0, max = 100 } = props;
  const rootLabel = props['aria-label'];

  const thumbCount = useMemo(() => {
    const current = Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max];
    return Math.max(current.length, 1);
  }, [value, defaultValue, min, max]);

  /*
   * A single-thumb slider also falls back to the root's `aria-label`, so the
   * obvious `<Slider aria-label="Weight" />` is nameable without anyone having
   * to know that the role lives on the thumb. With two or more thumbs there is
   * no honest way to split one string, so the fallback stops there.
   */
  function labelFor(index: number): string | undefined {
    if (Array.isArray(thumbLabel)) return thumbLabel[index];
    if (typeof thumbLabel === 'string') return thumbLabel;
    return thumbCount === 1 ? rootLabel : undefined;
  }

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      className={cn(
        'relative flex w-full touch-none items-center select-none',
        'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        'data-[disabled]:opacity-70',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          'relative grow overflow-hidden rounded-full bg-muted',
          'data-[orientation=horizontal]:h-1 data-[orientation=horizontal]:w-full',
          'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1',
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            'absolute bg-primary',
            'data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: thumbCount }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          aria-label={labelFor(index)}
          className={cn(
            'block size-4 shrink-0 rounded-full border-2 border-accent bg-card shadow-(--shadow-raised)',
            'transition-transform duration-(--duration-fast) ease-(--ease-out-expo)',
            'hover:scale-110 active:scale-105',
            'disabled:pointer-events-none',
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
}
